<template>
  <div>
    <v-row align="center" class="mb-2">
      <v-col>
        <v-text-field
          v-model="pageSearchQuery"
          :label="$t('searchByPageNumber')"
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
          hide-details
          ref="pageSearchInput"
        />
      </v-col>
      <v-col cols="auto" align="right">
        <Icon
        icon="mdi-refresh"
        :small="false"
        elementId="account-ticket-books-refresh"
        :tooltipText="$t('refresh')"
        @icon-clicked="$emit('refresh-ticket-books')"/>
        <Icon
        v-if="accountId != null"
        :icon="archiveFilter.icon"
        icon-color="error"
        @icon-clicked="toggleArchive"
        :small="false"
        :tooltipText="archiveFilter.tooltipText"
        :overrideClickable="pageSearchQuery != null"/>
        <Icon
        margin=""
        :icon="asList ? 'mdi-list-box' : 'mdi-list-box-outline'"
        @icon-clicked="toggleView"
        :small="false"
        :tooltipText="asList ? $t('gridView') :$t('listView')"/>
      </v-col>
    </v-row>
    <LoadableComponent :loading="loading" :height="256">
      <div v-if="!loading && ticketBooks.length > 0">
        <v-row v-if="!asList">
          <v-col cols="12" md="3" v-for="tb in sortedTicketBooks" :key="`${tb.name}-${tb.status}`" class="d-flex">
            <v-card outlined rounded class="flex-grow-1 d-flex flex-column" :class="{ 'archived-card': tb.status === TicketBookStatus.Inactive.value }">
              <v-card-title class="flex-nowrap align-start">
                <div class="flex-grow-1" style="min-width: 0">
                  <span class="subtitle-1">{{ tb.name }}</span>
                  <Icon
                  :small="false"
                  class="ml-2"
                  icon="mdi-book-open-page-variant"
                  :tooltipText="$t('viewPages')"
                  @icon-clicked="viewPages(tb)"/>
                </div>
                <div class="text-no-wrap flex-shrink-0 ml-2">
                  <Icon
                  v-if="!(readonly || accountId == undefined)"
                  icon="mdi-pencil"
                  :tooltipText="$t('editTicketBook')"
                  @icon-clicked="editTicketBook(tb)"/>
                  <Icon
                  v-if="!(readonly || accountId == undefined) && tb.status !== TicketBookStatus.Inactive.value && tb.totalPageCount > 0"
                  icon="mdi-book-plus"
                  :tooltipText="$t('extendTicketBook')"
                  @icon-clicked="extendTicketBook(tb)"/>
                  <Icon
                  v-if="!readonly"
                  icon="mdi-delete-forever"
                  :tooltipText="$t('deleteTicketBook')"
                  @icon-clicked="deleteTicketBook(tb)"/>
                </div>
              </v-card-title>
              <v-card-text class="flex-grow-1">
                <v-row>
                  <v-col cols="12" md="8">
                    <v-row class="mt-n8">
                      <v-col cols="12">
                        {{ $t('pages') }}: {{ pageRangeText(tb) }}
                      </v-col>
                    </v-row>
                    <v-row class="mt-n7" v-if="tb.status === TicketBookStatus.Inactive.value">
                      <v-col cols="12">
                        {{ $t('archived') }}
                        <Icon
                        icon="mdi-archive"
                        :tooltipText="$t('archived')"
                        iconColor="red"/>
                      </v-col>
                    </v-row>
                    <v-row class="mt-n7" v-else-if="tb.status === TicketBookStatus.InUse.value">
                      <v-col cols="12">
                        <ContractKeyTooltip
                        v-if="tb.contractId != null"
                        :type="tb.contractType"
                        :fromAccount="tb.contractFromAccount"
                        :destinationAccount="tb.contractDestinationAccount"
                        :account="tb.contractAccount"/>
                        <template v-else>
                          {{ tb.tract }}
                          <Icon
                          :icon="IconHelpers.getIconForObjectType('tract')"
                          :tooltipText="$t('inUse')"
                          iconColor="info"/>
                        </template>
                      </v-col>
                    </v-row>
                    <v-row class="mt-n7" v-else>
                      <v-col cols="12">
                        {{ $t('available') }}
                      </v-col>
                    </v-row>
                    <v-row class="mt-n7">
                      <v-col cols="12">
                        {{ pageCountText(tb) }}
                      </v-col>
                    </v-row>
                    <v-row class="mt-n7">
                      <v-col cols="12">
                        {{ pagesAvailableText(tb) }}
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="4" align="right">
                    <v-tooltip bottom color="success">
                      <template #activator="{ on }">
                        <v-progress-circular
                        :rotate="-90"
                        width="14"
                        size="40"
                        :style="{ stroke: 'black' }"
                        color="success"
                        :value="tb.percentage"
                        v-on="on"
                        />
                      </template>
                      <span class="subtitle-1 white--text">{{ $t('percentageOfPagesUsed', { percentage: tb.percentage.toFixed(2) }) }}</span>
                    </v-tooltip>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12">
            <TicketBookTable
            :loading="loading"
            :ticketBooks="sortedTicketBooks"
            :headers="tableHeaders"
            :readonly="readonly"
            :accountId="accountId"
            @delete-ticket-book="deleteTicketBook($event)"
            @edit-ticket-book="editTicketBook($event)"
            @extend-ticket-book="extendTicketBook($event)"
            @view-pages="viewPages($event)"
            />
          </v-col>
        </v-row>
        <v-dialog width="400px" v-model="confirmDeleteDialog">
          <ConfirmDialog
          :title="$t('deleteTicketBook')"
          :body="$t('deleteTicketBookBody', { ticketBookName: focusedTicketBook?.name })"
          @confirm="confirmDeleteTicketBook"
          @cancel="closeDialogs()"/>
        </v-dialog>
        <v-dialog width="600px" v-model="viewPagesDialog">
          <TicketBookDetail
          v-if="viewPagesDialog"
          :ticketBook="focusedTicketBook"
          :readonly="readonly || accountId == undefined || focusedTicketBook?.status === TicketBookStatus.Inactive.value"
          :accountId="accountId"
          @close="closeDialogs()"
          @split-ticket-book="$emit('split-ticket-book', $event)"/>
        </v-dialog>
        <v-dialog width="400px" v-model="editTicketBookDialog">
          <TicketBookEditor
          v-if="editTicketBookDialog"
          :propTicketBook="focusedTicketBook"
          @close="closeDialogs()"
          @edit-ticket-book="editedTicketBook"/>
        </v-dialog>
        <v-dialog width="500px" v-model="extendTicketBookDialog">
          <TicketBookExtendDialog
          v-if="extendTicketBookDialog"
          :ticketBook="focusedTicketBook"
          :ticketBooks="ticketBooks"
          @confirm="confirmExtendTicketBook"
          @close="closeDialogs()"/>
        </v-dialog>
      </div>
      <span v-else>
        <slot name="noData">
          {{ $t('noTicketBooks') }}
        </slot>
      </span>
    </LoadableComponent>
  </div>
</template>
<script>

import { TicketBookStatus } from '@/utils/Enumerations'
import { ticketBookHeaders, ticketBookCustomCells } from '@/headers/Account'
import { pageRangeString } from '@/utils/NumericMutations'
import IconHelpers from '@/utils/IconHelpers'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
export default {
  name: 'AccountTicketBooks',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    TicketBookDetail: () => import('@/components/account/ticketBooks/TicketBookDetail.vue'),
    TicketBookEditor: () => import('@/components/account/ticketBooks/TicketBookEditor.vue'),
    TicketBookExtendDialog: () => import('@/components/account/ticketBooks/TicketBookExtendDialog.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    TicketBookTable: () => import('@/components/account/ticketBooks/TicketBookTable.vue'),
    ContractKeyTooltip: () => import('@/components/contract/ContractKeyTooltip.vue')
  },

  props: {
    ticketBooks: {
      type: Array,
      default: () => []
    },

    loading: {
      type: Boolean,
      default: false
    },

    readonly: {
      type: Boolean,
      required: true
    },

    accountId: {
      type: Number,
      default: undefined
    }
  },

  data: () => ({
    TicketBookStatus,
    IconHelpers,
    confirmDeleteDialog: false,
    focusedTicketBook: undefined,
    viewPagesDialog: false,
    editTicketBookDialog: false,
    extendTicketBookDialog: false,
    showArchived: false,
    asList: localStorage.getItem(LocalStorageKeys.TICKET_BOOKS_AS_LIST) === 'true',
    pageSearchQuery: null
  }),

  computed: {
    sortedTicketBooks () {
      const sortOrder = {
        [this.TicketBookStatus.InUse.value]: 0,
        [this.TicketBookStatus.Available.value]: 1,
        [this.TicketBookStatus.Inactive.value]: 2
      }
      return [...this.ticketBooks]
        .filter(tb => this.showArchived || tb.status !== TicketBookStatus.Inactive.value || this.pageSearchQuery != null)
        .filter(tb => {
          if (this.pageSearchQuery == null || this.pageSearchQuery === '') {
            return true
          }
          const searchPage = parseInt(this.pageSearchQuery)
          return searchPage >= tb.firstPage && searchPage <= tb.lastPage
        })
        .map(tb => ({
          ...tb,
          mappedPages: { firstPage: tb.firstPage, lastPage: tb.lastPage },
          percentage: tb.totalPageCount > 0 ? (tb.totalPageCount - tb.availablePageCount) / tb.totalPageCount * 100 : 0
        }))
        .sort((a, b) => {
          if (a.status === b.status) { return a.firstPage - b.firstPage }
          const statusA = sortOrder[a.status]
          const statusB = sortOrder[b.status]
          return statusA - statusB
        })
    },

    tableHeaders () {
      return ticketBookHeaders()
    },

    archiveFilter () {
      return {
        icon: this.showArchived || this.pageSearchQuery != null ? 'mdi-archive-outline' : 'mdi-archive',
        tooltipText: this.pageSearchQuery != null ? this.$t('showingAll')
          : this.showArchived ? this.$t('hideArchived') : this.$t('showArchived')
      }
    }
  },

  methods: {
    ticketBookCustomCells,
    deleteTicketBook (tb) {
      const { availablePageCount, totalPageCount } = tb
      if (availablePageCount < totalPageCount) {
        this.setSnackError(this.$t('cannotDeleteTicketBookWithUsedPages'))
        return
      }
      this.focusedTicketBook = tb
      this.confirmDeleteDialog = true
    },

    confirmDeleteTicketBook () {
      this.$emit('delete-ticket-book', this.focusedTicketBook)
      this.closeDialogs()
    },

    closeDialogs () {
      this.confirmDeleteDialog = false
      this.viewPagesDialog = false
      this.editTicketBookDialog = false
      this.extendTicketBookDialog = false
      this.focusedTicketBook = undefined
    },

    editTicketBook (tb) {
      this.focusedTicketBook = tb
      this.editTicketBookDialog = true
    },

    extendTicketBook (tb) {
      this.focusedTicketBook = tb
      this.extendTicketBookDialog = true
    },

    confirmExtendTicketBook (payload) {
      this.$emit('extend-ticket-book', payload)
    },

    pageRangeText (tb) {
      return tb.totalPageCount === 0
        ? this.$t('none')
        : pageRangeString(tb.firstPage, tb.lastPage)
    },

    pageCountText (tb) {
      return tb.totalPageCount === 1
        ? this.$t('singlePage')
        : this.$t('xPages', { x: tb.totalPageCount })
    },

    pagesAvailableText (tb) {
      return tb.availablePageCount === 1
        ? this.$t('singlePageAvailable')
        : this.$t('xPagesAvailable', { x: tb.availablePageCount })
    },

    viewPages (tb) {
      this.viewPagesDialog = true
      this.focusedTicketBook = tb
    },

    editedTicketBook (tb) {
      this.$emit('edit-ticket-book', tb)
    },

    toggleArchive () {
      this.showArchived = !this.showArchived
    },

    toggleView () {
      this.asList = !this.asList
      localStorage.setItem(LocalStorageKeys.TICKET_BOOKS_AS_LIST, this.asList)
    }
  }
}
</script>

<style scoped>
.archived-card {
  opacity: 0.8;
  background-color: #f5f5f5;
}
</style>
